import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectBookingPageSettings,
  selectBookingPageSettingsError,
  selectBookingPageSettingsStatus,
} from "../../features/trial-requests/trial-requests-slice";
import { updateBookingPageSettings } from "../../features/trial-requests/slice-extensions/trial-requests-slice-extension";
import { BookingPageLinkContract } from "../../features/trial-requests/contracts/BookingPageLinkContract";
import {
  BOOKING_PAGE_LINK_KINDS,
  BookingPageLinkKind,
} from "../../features/trial-requests/enums/BookingPageLinkKind";
import { toUpdateRequest } from "../../features/trial-requests/utils/bookingPage";

const MAX_LINKS = 10;
const URL_PATTERN = /^https?:\/\/.+/i;

const normalize = (links: BookingPageLinkContract[]): BookingPageLinkContract[] =>
  links
    .map((link) => ({
      kind: link.kind,
      url: link.url.trim(),
      label: link.label?.trim() ? link.label.trim() : null,
    }))
    .filter((link) => link.url !== "");

/**
 * Social & web links shown on the landing page and the links page. The same
 * platform may appear more than once — the optional label tells them apart.
 */
const WebsiteLinksCard = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const settings = useAppSelector(selectBookingPageSettings);
  const status = useAppSelector(selectBookingPageSettingsStatus);
  const error = useAppSelector(selectBookingPageSettingsError);
  const [links, setLinks] = useState<BookingPageLinkContract[]>([]);

  useEffect(() => {
    if (!settings) return;
    setLinks(settings.links);
  }, [settings]);

  const normalized = normalize(links);
  const dirty = !!settings && JSON.stringify(normalized) !== JSON.stringify(normalize(settings.links));
  const allValid = normalized.every((link) => URL_PATTERN.test(link.url));

  const update = (index: number, patch: Partial<BookingPageLinkContract>) => {
    setLinks((current) => current.map((link, i) => (i === index ? { ...link, ...patch } : link)));
  };

  const handleSave = () => {
    if (!settings) return;
    dispatch(
      updateBookingPageSettings({
        ...toUpdateRequest(settings),
        links: normalized,
      }),
    );
  };

  if (!settings?.enabled) {
    return null;
  }

  return (
    <div className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10 lg:p-6">
      <h2 className="text-lg font-semibold text-grey-20 dark:text-white">
        {t("website.linksTitle", "Social & web links")}
      </h2>
      <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">
        {t("website.linksIntro", "Your website and social profiles — shown on your landing page and links page. The label is optional (\"My blog\").")}
      </p>

      {links.map((link, index) => (
        <div key={index} className="mt-3 flex flex-wrap items-center gap-2">
          <select
            className="input w-36 shrink-0"
            value={link.kind}
            onChange={(event) => update(index, { kind: event.target.value as BookingPageLinkKind })}
          >
            {BOOKING_PAGE_LINK_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {t(`website.linkKind.${kind}`, kind)}
              </option>
            ))}
          </select>
          <input
            className="input min-w-40 flex-1"
            value={link.url}
            maxLength={300}
            onChange={(event) => update(index, { url: event.target.value })}
            placeholder="https://…"
          />
          <input
            className="input w-40 shrink-0"
            value={link.label ?? ""}
            maxLength={60}
            onChange={(event) => update(index, { label: event.target.value })}
            placeholder={t("website.linkLabelPlaceholder", "Label (optional)")}
          />
          <button
            type="button"
            className="btn btn-secondary shrink-0"
            onClick={() => setLinks((current) => current.filter((_, i) => i !== index))}
            aria-label={t("website.removeLink", "Remove link")}
          >
            <Trash2 size={16} />
          </button>
        </div>
      ))}

      {!allValid && (
        <p className="field-error mt-2">
          {t("website.linkInvalid", "Links must start with http:// or https://")}
        </p>
      )}

      {links.length < MAX_LINKS && (
        <button
          type="button"
          className="btn btn-secondary mt-4"
          onClick={() => setLinks((current) => [...current, { kind: "Website", url: "", label: null }])}
        >
          <Plus size={16} />
          {t("website.addLink", "Add link")}
        </button>
      )}

      {error && <p className="field-error mt-2">{error}</p>}

      {dirty && (
        <button
          type="button"
          className="btn btn-primary mt-4 ml-2"
          onClick={handleSave}
          disabled={status === "loading" || !allValid}
        >
          {t("website.save", "Save changes")}
        </button>
      )}
    </div>
  );
};

export default WebsiteLinksCard;
